"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import cx from "classnames";
import Label from "@ingka/label";
import Listbox from "@ingka/list-box";
import ComboboxTrigger from "./ComboboxTrigger.mjs";
import { useCombobox, clearActiveItems } from "./utils/index.mjs";
const debounce = (func, timeout) => {
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func();
    }, timeout);
  };
};
const Combobox = (props) => {
  const {
    prefix = "",
    label,
    children,
    className,
    disabled,
    multiple = false,
    placeholder,
    listboxId,
    clearButtonLabel,
    labelId,
    value,
    onListBoxChange,
    ...otherProps
  } = props;
  const baseClass = `${prefix}combobox`;
  const [isOpen, setIsOpen] = useState(false);
  const classes = cx(
    `${baseClass}`,
    isOpen && `${baseClass}--open`,
    disabled && `${baseClass}--disabled`,
    className
  );
  const triggerProps = {
    prefix,
    disabled,
    placeholder,
    clearButtonLabel
  };
  const [combobox, trigger] = useCombobox();
  useEffect(() => {
    const outsideComboboxClick = (e) => {
      if (!combobox.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (window) {
      if (isOpen) {
        document.addEventListener("click", outsideComboboxClick);
      } else {
        document.removeEventListener("click", outsideComboboxClick);
      }
    }
    return () => {
      document.removeEventListener("click", outsideComboboxClick);
    };
  }, [isOpen, combobox]);
  useEffect(() => {
    if (!isOpen) {
      clearActiveItems(
        combobox,
        prefix,
        trigger
      );
    }
  }, [isOpen, combobox, prefix, trigger]);
  return /* @__PURE__ */ jsxs("div", { className: classes, ...otherProps, ref: combobox, children: [
    label && /* @__PURE__ */ jsx(
      Label,
      {
        className: `${baseClass}__label`,
        id: labelId,
        label,
        prefix
      }
    ),
    children && /* @__PURE__ */ jsx(
      Listbox,
      {
        onKeyDown: () => {
          trigger.current?.focus();
        },
        trigger: /* @__PURE__ */ jsx(
          ComboboxTrigger,
          {
            labelId,
            combobox,
            setIsOpen,
            isOpen,
            listboxId,
            comboboxRef: trigger,
            ...triggerProps
          }
        ),
        id: listboxId,
        multiple,
        domFocus: false,
        onChange: (selectedItems) => {
          if (!multiple) {
            setIsOpen(false);
          }
          if (onListBoxChange) {
            onListBoxChange(selectedItems);
          } else if (trigger.current && !value) {
            trigger.current.focus();
          }
        },
        onMouseMove: debounce(() => {
          clearActiveItems(
            combobox,
            prefix,
            trigger
          );
        }, 100),
        "aria-labelledby": labelId,
        prefix,
        open: isOpen,
        value,
        children
      }
    )
  ] });
};
Combobox.displayName = "Combobox";
var Combobox_default = Combobox;
export {
  Combobox_default as default
};
